import { FC } from 'react';

import { LorcanaOptionIcon } from './LorcanaOptionIcon';
import { MagicTypeOptionIcon } from './MagicTypeOptionIcon';
import { OnePieceColorIcon } from './OnePieceColorIcon';
import { PokemonOptionIcon } from './PokemonOptionIcon';
import { StarWarsOptionIcon } from './StarWarsOptionIcon';
import { cn } from '../../../utils';

interface Props {
    readonly iconKey?: string | null;
    readonly label: string;
    readonly className?: string;
    readonly fallback?: boolean;
}

interface IconDefinition {
    glyphClassName: string;
    containerClassName: string;
}

const ICONS: Record<string, IconDefinition> = {
    mtg_white: {
        glyphClassName: 'ms ms-w ms-cost ms-shadow',
        containerClassName: 'text-slate-950',
    },
    mtg_blue: {
        glyphClassName: 'ms ms-u ms-cost ms-shadow',
        containerClassName: 'text-slate-950',
    },
    mtg_black: {
        glyphClassName: 'ms ms-b ms-cost ms-shadow',
        containerClassName: 'text-slate-950',
    },
    mtg_red: {
        glyphClassName: 'ms ms-r ms-cost ms-shadow',
        containerClassName: 'text-slate-950',
    },
    mtg_green: {
        glyphClassName: 'ms ms-g ms-cost ms-shadow',
        containerClassName: 'text-slate-950',
    },
    mtg_colorless: {
        glyphClassName: 'ms ms-c ms-cost ms-shadow',
        containerClassName: 'text-slate-950',
    },
};

export const PropertyOptionIcon: FC<Props> = ({
    iconKey,
    label,
    className,
    fallback = false,
}) => {
    if (!iconKey && !fallback) return null;

    if (iconKey?.startsWith('one_piece_color_')) {
        return <OnePieceColorIcon iconKey={iconKey} className={className} />;
    }

    if (iconKey?.startsWith('mtg_type_')) {
        return <MagicTypeOptionIcon iconKey={iconKey} className={className} />;
    }

    if (iconKey === 'land' || iconKey === 'token') {
        return (
            <MagicTypeOptionIcon
                iconKey={`mtg_type_${iconKey}`}
                className={className}
            />
        );
    }

    if (iconKey?.startsWith('pokemon_')) {
        return <PokemonOptionIcon iconKey={iconKey} className={className} />;
    }

    if (iconKey?.startsWith('lorcana_')) {
        return <LorcanaOptionIcon iconKey={iconKey} className={className} />;
    }

    if (iconKey?.startsWith('swu_')) {
        return <StarWarsOptionIcon iconKey={iconKey} className={className} />;
    }

    const definition = iconKey ? ICONS[iconKey] : undefined;

    if (!definition) {
        return (
            <span
                aria-hidden="true"
                className={cn(
                    'inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full border bg-muted text-[10px] font-semibold uppercase',
                    className,
                )}
            >
                {label.slice(0, 1)}
            </span>
        );
    }

    return (
        <span
            aria-hidden="true"
            className={cn(
                'inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full',
                definition.containerClassName,
                className,
            )}
        >
            <i className={cn('text-base', definition.glyphClassName)} />
        </span>
    );
};
